import { useEffect, useEffectEvent, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAnalytics } from '@/services/analytics';
import { useObjectTypesCached } from '@/services/db/object-type/queries';
import { getWorkspaceTagsQueryOptions } from '@/services/db/tag/queries';
import { isEmptyString } from '@/utils/string';
import type { BroadcastsListState } from './use-broadcasts-list';

/**
 * The names of the catalog entries `ids` selects, or `null` when there is nothing to report,
 * including when none of the ids is in the catalog, as Ember's `getModelEntryNameFromId` reads it.
 */
function selectedNames(
  ids: readonly string[] | undefined,
  catalog: ReadonlyArray<{ id: string | number; name: string }>,
) {
  if (ids == null || ids.length === 0 || catalog.length === 0) {
    return null;
  }
  const names = catalog.filter((entry) => ids.includes(String(entry.id))).map((entry) => entry.name);
  return names.length === 0 ? null : names;
}

/**
 * Reports "Broadcast: List Viewed" each time the showing tab loads a new set of results —
 * a search, filter, sort or page change, or switching tabs — and not while the welcome
 * state is up. A refetch of the same results (a window refocus) is not a new view.
 */
export function useListViewedAnalytics({
  workspaceId,
  activeTab,
  activeSearch,
  activeQuery,
  shouldDisplayWelcome,
}: BroadcastsListState) {
  const { track } = useAnalytics();
  const tags = useQuery(getWorkspaceTagsQueryOptions(workspaceId)).data ?? [];
  const objectTypes = useObjectTypesCached();
  const viewKey = activeTab == null || activeSearch == null ? null : `${activeTab}:${JSON.stringify(activeSearch)}`;
  const isLoaded = activeQuery.isSuccess && !activeQuery.isFetching;

  // The catalogs only resolve names for the view being reported, so one arriving later is not
  // a new view: they are read at report time rather than re-triggering it.
  const reportView = useEffectEvent(() => {
    if (activeSearch == null) {
      return;
    }
    const tagNames = selectedNames(activeSearch.tagIds, tags);
    const objectTypeNames = selectedNames(activeSearch.objectTypeIds, objectTypes);
    void track('broadcast', 'listViewed', {
      'Broadcast Type': activeTab === 'api-triggered' ? 'Triggered Broadcast' : 'Newsletter',
      'Filtered By Broadcast Search': !isEmptyString(activeSearch.search),
      // Unset keys are dropped when the event is serialized, so an unfiltered view sends neither.
      'Filtered By Campaign Tag(s)': tagNames ?? undefined,
      'Filtered by Object Types': objectTypeNames ?? undefined,
    });
  });

  // A refetch of the same results (a reconnect, or an invalidation after a row action) loads
  // the same view again, so the last one reported is remembered rather than re-reported.
  const lastReported = useRef<string | null>(null);
  useEffect(() => {
    if (viewKey == null || !isLoaded || shouldDisplayWelcome || viewKey === lastReported.current) {
      return;
    }
    lastReported.current = viewKey;
    reportView();
  }, [viewKey, isLoaded, shouldDisplayWelcome]);
}
